<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  //used base: https://tailwindui.com/components/application-ui/lists/stacked-lists#component-6a56f3c03589dfa48dc4e7eec7fbf6d0
  import type Popover from 'primevue/popover'
  import type { EmployeeNotificationJsonldDefaultRead } from '@/api'
  import { InfoIcon, NewIcon } from '@/config/icons'

  import ContextMenu from 'primevue/contextmenu'
  import type { MenuItem } from 'primevue/menuitem'
  import { type LocalNotificationBaseNonGeneric, useInAppNotificationService } from '@/services/InAppNotificationService'
  import SpinnerDefault from '@/elements/SpinnerDefault.vue'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import OverlayBadge from 'primevue/overlaybadge'
  import { useRouter } from 'vue-router'

  const notificationService = useInAppNotificationService()

  const overlay = ref<InstanceType<typeof Popover>>()
  const r = useRouter()

  function toggleOverlay(event: Event) {
    overlay.value?.toggle(event)
  }

  type AnyNotification = EmployeeNotificationJsonldDefaultRead | LocalNotificationBaseNonGeneric
  const refreshKey = ref(0)

  const contextMenuSelected = ref<AnyNotification>()

  function onContextMenu(event: Event, notification: AnyNotification) {
    contextMenuSelected.value = notification
    contextMenuRef.value!.show(event)
  }

  const contextMenuItems = computed<MenuItem[] | undefined>(() => {
    if (!contextMenuSelected.value) return undefined

    const n = contextMenuSelected.value

    const cmdOpenObject: MenuItem = {
      label: 'Objekt öffnen',
      command: async () => {
        const route = r.resolve({
          name: 'effort_details',
          params: { id: n.data.effort }
        })
        window.open(route.href, '_blank')
      },
      icon: 'InternalLinkIcon'
    }

    const cmdMarkAsRead: MenuItem = {
      label: 'Als gelesen markieren',
      command: () => {
        void notificationService.setAsRead(contextMenuSelected.value!, true)
      },
      icon: 'EyeCheckIcon'
    }

    const cmdMarkAsUnread: MenuItem = {
      label: 'Als ungelesen markieren',
      command: () => {
        void notificationService.setAsRead(contextMenuSelected.value!, false)
      },
      icon: 'EyeOffIcon'
    }

    const cmdRemove: MenuItem = {
      label: 'Löschen',
      command: () => {
        notificationService.removeLocal(contextMenuSelected.value as LocalNotificationBaseNonGeneric)
      },
      icon: 'DeleteIcon'
    }

    const actions: MenuItem[] = [cmdOpenObject]
    actions.push(n.read ? cmdMarkAsUnread : cmdMarkAsRead)
    if (n.read && (n as LocalNotificationBaseNonGeneric).deletable) actions.push(cmdRemove)
    return actions
  })
  const contextMenuRef = ref<InstanceType<typeof ContextMenu>>()

  //this ensures notificationService gets ready on hot reload
  if (!notificationService.IsReady) {
    const watchStop = watch(
      notificationService.IsReady,
      (a: boolean) => {
        if (!a) void notificationService.refresh()
        else watchStop()
      },
      { immediate: true }
    )
  }

  const shownReadNotificationsCount = ref<number>(0)

  const onShowReadNotificationsClick = (): void => {
    if (shownReadNotificationsCount.value < notificationService.ReadNotificationCount.value)
      shownReadNotificationsCount.value += notificationService.ReadNotificationCount.value - shownReadNotificationsCount.value >= 10 ?
        10 : notificationService.ReadNotificationCount.value - shownReadNotificationsCount.value
  }

  const onHideReadNotificationsClick = () => {
    shownReadNotificationsCount.value = 0
  }

  const onPopoverHide = () => {
    shownReadNotificationsCount.value = 0
  }

  const onDoubleClick = async (notif) => {
    await notificationService.setAsRead(notif)
    refreshKey.value++
  }
</script>

<template>
  <div
    id="notification-header-comp"
    class="flex-0 flex items-center md:min-w-fit"
    @click="toggleOverlay">
    <template v-if="!notificationService.IsReady.value">
      <SpinnerDefault />
    </template>
    <template v-else>
      <OverlayBadge
        v-if="notificationService.HasUnreadNotifications.value"
        size="small">
        <ButtonDefault icon="NotificationIcon" outlined severity="secondary" />
      </OverlayBadge>

      <ButtonDefault v-else icon="NotificationIcon" outlined severity="secondary" />
      <Popover
        ref="overlay"
        :pt="{
          root: { class: ['max-h-[66vh] w-full md:max-w-[500px] overflow-x-hidden overflow-y-auto !border !rounded-md !shadow-xl text-gray-900'] },
          content: { class: ['md:p-0', 'p-1'] }
        }"
        :pt-options="{ mergeProps: true, mergeSections: false }"
        @hide="onPopoverHide">
        <!-------------------->
        <ul
          :key="refreshKey"
          class="divide-y divide-gray-100"
          role="list">
          <li v-if="notificationService.Notifications.value.length == 0" class="px-1 text-center py-2">(keine Benachrichtigungen)</li>
          <li v-else-if="!notificationService.HasUnreadNotifications.value" class="px-1 flex items-center justify-center py-2">
            <Message size="small" class="w-full flex justify-center">
              <template #icon>
                <InfoIcon></InfoIcon>
              </template>
              <div class="text-center items-center">
                keine neue Benachrichtigungen
              </div>
            </Message>
          </li>
          <template v-else>
            <div class="font-semibold py-2">
              <span class="flex items-center justify-center">Neue</span>
            </div>
            <li
              v-for="notification in notificationService.UnreadNotifications.value"
              :key="notification.id"
              :class="{ 'opacity-50': notification.read, 'bg-rose-100': notification.priority && !notification.read }"
              :data-notification-id="notification.id"
              :data-notification-type="notification.type"
              class="flex items-center justify-between gap-x-2 px-2 py-1 hover:bg-gray-200"
              @click="contextMenuRef?.hide"
              @contextmenu.prevent="onContextMenu($event, notification)"
              @dblclick.prevent="onDoubleClick(notification)">
              <NewIcon
                v-if="!notification.read"
                fill-color="#F59E0B" />

              <div class="min-w-0 flex-1">
                <div class="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                  <p class="whitespace-nowrap">
                    {{ $formatter.DateTimeRelative(notification.createdAt, { threshold: { d: 1 } }) }}
                  </p>
                </div>
                <component
                  :is="notificationService.getRenderComponent(notification.type)"
                  :key="notification.id"
                  :notification="notification"
                  class="text-sm font-semibold leading-6 text-gray-900"
                  @close="overlay?.hide()"
                  @read="notificationService.setAsRead(notification)" />
              </div>
            </li>
          </template>

          <template v-if="shownReadNotificationsCount">
            <div class="font-semibold py-2">
              <span class="flex items-center justify-center">Gelesene</span>
            </div>
            <li
              v-for="notification in notificationService.ReadNotifications.value.slice(0, shownReadNotificationsCount)"
              :key="notification.id"
              :class="{ 'opacity-50': notification.read, 'bg-rose-100': notification.priority && !notification.read }"
              :data-notification-id="notification.id"
              :data-notification-type="notification.type"
              class="flex items-center justify-between gap-x-2 px-2 py-1 hover:bg-gray-200"
              @click="contextMenuRef?.hide"
              @contextmenu.prevent="onContextMenu($event, notification)"
              @dblclick.prevent="notificationService.setAsRead(notification)">
              <NewIcon
                v-if="!notification.read"
                fill-color="#F59E0B" />

              <div class="min-w-0 flex-1">
                <div class="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                  <p class="whitespace-nowrap">
                    {{ $formatter.DateTimeRelative(notification.createdAt, { threshold: { d: 1 } }) }}
                  </p>
                </div>
                <Component
                  :is="notificationService.getRenderComponent(notification.type)"
                  :key="notification.id"
                  :notification="notification"
                  class="text-sm font-semibold leading-6 text-gray-900"
                  @close="overlay?.hide()"
                  @read="notificationService.setAsRead(notification)" />
              </div>
            </li>
          </template>
          <template v-if="notificationService.HasReadNotifications.value">
            <li class="py-1 px-1">
              <ButtonDefault
                v-if="notificationService.ReadNotificationCount.value - shownReadNotificationsCount > 0"
                class="w-full px-1"
                size="small"
                :label="shownReadNotificationsCount > 0 ? 'Mehr anzeigen' : 'Gelesene anzeigen'"
                outlined
                icon="NotificationHistoryIcon"
                @click="onShowReadNotificationsClick" />
              <ButtonDefault
                v-else
                class="w-full px-1"
                size="small"
                label="Gelesene ausblenden"
                outlined
                icon="NotificationHideIcon"
                @click="onHideReadNotificationsClick" />
            </li>
          </template>
        </ul>
        <!-------------------->
        <ContextMenu
          ref="contextMenuRef"
          :model="contextMenuItems">
          <template #itemicon="itm">
            <component
              :is="itm.item.icon"
              :class="[itm.class]" />
          </template>
        </ContextMenu>

        <!-- TabView :active-index="0">
                <TabPanel header="ungelesene">
                    <NotificationList :notifications="notifications?.filter((n) => !n.read)" />
                </TabPanel>
                <TabPanel header="alle">
                    <NotificationList :notifications="notifications" />
                </TabPanel>
            </TabView -->
      </Popover>
    </template>
  </div>
</template>

<style scoped>
  :deep(.p-badge.p-badge-dot) {
    width: 0.75rem;
    height: 0.75rem;
    min-width: 0.5rem;
    padding: 0;
  }
</style>
